import React, { useEffect } from 'react';
import { useStore } from '../../../../../../stores/root';
import PaymentMethods from '../../../../../../manifests/paymentMethods';
import moment from 'moment';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../services/general/error';
import ValidationService from '../../../../../../services/validation';
import GeneralService from '../../../../../../services/general';
import OutcomeService from '../../../../../../services/finance/outcomes';
import OutcomePaymentService from '../../../../../../services/finance/outcomes/payments';

// props

// components
import { DatePicker, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { IOutcomePaymentResourceProps } from '../../../../../../props/finance/outcomes/payments';
import SelectInvoice from '../../../../../uiframeworks/forms/selectInvoice';
import SelectBankAccount from '../../../../../uiframeworks/forms/bankaccounts';
import EmployeePayslipService from '../../../../../../services/employees/payslip';
import Text from '../../../../../typography/text';
import PermissionsService from '../../../../../../services/permissions';
import DepositService from '../../../../../../services/finance/deposits';
import { IVendorResourceShortProps } from '../../../../../../props/data/vendors';
import Details from '../../../../../typography/details';
import LoadingComponent from '../../../../../feedbacks/loading';

interface IOutcomePaymentFormProps {
    //invoiceIds?: string[];
    invoiceId?: string;
    payslipId?: string;
    paymentId?: string;
    onDismissed(refresh?: boolean): void;
}

/*type FormDataProps = {
    method: string;
    amount: string;
    totalAmount: string;
    notes: string;
    tax: string;
    targetBankAccount?: string;
    targetVirtualAccountNumber: string;
    dueDate: string;
}

type FormDataErrorProps = {
    method?: string;
    amount?: string;
    ppn?: string;
    pph?: string;
    totalAmount?: string;
    notes?: string;
    tax?: string;
}*/

export type FormDataProps = IOutcomePaymentResourceProps & {

};

const OutcomePaymentForm: React.FC<IOutcomePaymentFormProps> = (props: IOutcomePaymentFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [depositAmount, setDepositAmount] = React.useState<number | undefined>();
    const [data, setData] = React.useState<Partial<FormDataProps>>({
        id: GeneralService.guid(),
        method: "transfer"
    });
    const [selectedPurpose, setSelectedPurpose] = React.useState<'invoice' | 'payslip' | undefined>();
    const [showAddInvoice, setShowAddInvoice] = React.useState<boolean>(false);
    const [error, setError] = React.useState<any>({});

    const hasReadDepositPermission = PermissionsService.hasPermission(['deposits.read.all'], user.permissions);

    useEffect(() => {
        init();
    }, []);

    const retrieveDepositAmount = async (vendor?: IVendorResourceShortProps) => {
        setDepositAmount(undefined);

        if (hasReadDepositPermission && vendor) {
            const deposit = await DepositService.read("vendor", vendor.id);
            setDepositAmount(Number(deposit.amount));
        } else {
            setDepositAmount(0);
        }
    }

    const init = async () => {
        try {
            const _data = data;

            /*if (props.invoiceIds) {
                const _invoices = await Promise.all(props.invoiceIds.map(async (invoiceId) => {
                    return await OutcomeService.get(invoiceId);
                }));
                
                let amount = 0;
                let paymentInvoices: IOutcomePaymentInvoiceResourceShortProps[] = [];
                
                _invoices.forEach((_invoice) => {
                    amount += Number(_invoice.unpaid || "0") - Number(_invoice.pendingPayment || "0");
                    paymentInvoices.push({id: GeneralService.guid(), invoice: _invoice, amount: amount + ""})
                })
                
                _data.invoices = paymentInvoices;
                _data.totalAmount = amount + "";
                _data.amount = amount + "";
                _data.vendor = _invoices[0]?.vendor;
            }*/

            if (props.invoiceId) {
                const _invoice = await OutcomeService.get(props.invoiceId);

                _data.invoice = _invoice;
                _data.totalAmount = (Number(_invoice.unpaid) - Number(_invoice.pendingPayment)) + "";
                _data.amount = (Number(_invoice.unpaid) - Number(_invoice.pendingPayment)) + "";
                _data.vendor = _invoice.vendor;
                _data.targetSubsidiary = _invoice.targetSubsidiary;
                _data.targetEmployee = _invoice.targetEmployee;
            } else if (props.payslipId) {
                const _payslip = await EmployeePayslipService.read(props.payslipId);

                _data.payslip = _payslip;
                _data.totalAmount = (Number(_payslip.unpaidAmount) - Number(_payslip.pendingAmount)) + "";
                _data.amount = (Number(_payslip.unpaidAmount) - Number(_payslip.pendingAmount)) + "";
                _data.targetEmployee = _payslip.employee;
                _data.targetSubsidiary = _payslip.contract?.subsidiary;
            }

            retrieveDepositAmount(_data.vendor);

            setData({ ..._data });
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_invoices_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get invoices. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.amount || error.notes) {
            return true;
        } else if (data.amount === '' || !data.amount) {
            return true;
        } else if (data.method !== 'deposit' && !data.transferFrom) {
            return true;
        } else if (data.method === 'transfer' && !data.transferTo) {
            return true;
        } else if (data.method === 'virtualaccount' && (data.transferToVANumber || "").trim() === "") {
            return true;
        } else if (!data.invoice && !data.payslip) {
            return true;
        } else if (data.method === 'deposit' && (calculateDepositAmountLeftover() < 0 || (depositAmount && depositAmount <= 0))) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            const confirmed = window.confirm("Are you sure you want to submit this payment?");

            if (confirmed) {
                setSubmitting(true);
                const { amount, method, notes, dueDate, totalAmount, transferFrom, transferTo, transferToVANumber, invoice, payslip } = data;

                // create form data
                let fd = new FormData();
                if (notes) {
                    fd.append('notes', notes || "");
                }

                fd.append('amount', amount || "0");
                fd.append('dueDate', dueDate || "");
                fd.append('totalAmount', totalAmount || "0");

                fd.append('method', method || "Cash");

                if (method !== 'deposit' && transferFrom) {
                    fd.append("transferFromId", transferFrom.id);
                }

                if (method === 'transfer' && transferTo) {
                    fd.append('transferToId', transferTo.id);
                } else if (method === 'virtualaccount' && (transferToVANumber || "").trim() !== "") {
                    fd.append('transferToVANumber', (transferToVANumber || ""));
                }

                if (invoice) {
                    fd.append('invoiceId', invoice?.id || "");
                } else if (payslip) {
                    fd.append('payslipId', payslip?.id || "");
                }

                const payment = await OutcomePaymentService.create(fd);

                banner.add({
                    key: 'create_outcome_payment_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Payment Rp. ${GeneralService.getNumberWithSeparator(amount || "0")} recorded successfully`
                });
                props.onDismissed(true);
            }
        } catch (e) {
            setSubmitting(false);
        }
    }

    const calculateDepositAmountLeftover = () => {
        return Number(((depositAmount || 0) - Number(data.amount || "0")).toFixed(2));
    }

    return <Panel headerText={'Request Payment'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Request"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Recording payment ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    {!props.invoiceId && !props.payslipId ? <Dropdown label={'Select payment purpose'}
                        selectedKey={selectedPurpose}
                        options={[
                            { key: 'invoice', text: 'Invoice' },
                            { key: 'payslip', text: 'Payslip' }
                        ]} onChange={(ev, opt) => {
                            setSelectedPurpose(opt?.key as any);
                            setData({ ...data, invoice: undefined, paymentDate: undefined });
                        }} /> : null}
                    {props.payslipId ? <TextField label={"Payslip"}
                        required={true}
                        value={data.payslip ? data.payslip.employee.name + " - " + moment(data.payslip.payslipMonth, 'MM/YYYY').format("MMMM YYYY") : ""}
                        disabled={true} /> : null}
                    {props.invoiceId || selectedPurpose === 'invoice' ? <SelectInvoice label={'Related invoice'}
                        selected={data.invoice}
                        required={true}
                        onChange={(invoice) => {
                            data.invoice = invoice;
                            data.amount = invoice?.unpaid || "0";
                            setData({ ...data });
                        }}
                        disabled={props.invoiceId !== undefined || submitting} /> : null}
                    <Stack className='divider'></Stack>
                    <TextField label={"Requested payment amount"}
                        prefix='Rp'
                        required={true}
                        value={(data.amount || "") + ""}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.amount = (value || "");

                                const validation = ValidationService.combination(value, ['required', 'limit', 'number'], { maxChars: 18, max: Number(data.invoice?.unpaid || "0") });
                                error.amount = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.amount} />
                    <Stack className='divider'></Stack>
                    <Stack>
                        <Dropdown label={"Payment Method"}
                            required={true}
                            disabled={submitting}
                            selectedKey={data.method}
                            options={PaymentMethods}
                            onChange={(evt, opt) => {
                                data.method = opt ? opt.key as string : "";
                                data.transferTo = undefined;
                                data.transferToVANumber = undefined;
                                setData({ ...data });
                            }} />
                    </Stack>
                    {data.method === 'deposit' && !hasReadDepositPermission ? <>
                        <Text className={'color-red'}>You don't have permission to request payment using deposit.</Text>
                    </> : null}
                    {data.method === 'deposit' && hasReadDepositPermission && depositAmount === undefined ? <LoadingComponent label={'Retrieving vendor deposit amount ...'} labelPosition={'right'} spinnerPosition={'baseline'} size={SpinnerSize.small} /> : null}
                    {data.method === 'deposit' && hasReadDepositPermission && depositAmount !== undefined && depositAmount > 0 ? <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={'Current deposit amount'} text={`Rp. ${GeneralService.getNumberWithSeparator(depositAmount)}`} style={{ width: '50%' }} />
                        <Details title={'Deposit amount after payment'}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(calculateDepositAmountLeftover())}`}
                            errorMessage={calculateDepositAmountLeftover() < 0 ? 'Current deposit amount is not enough for this payment' : undefined}
                            style={{ width: '50%' }} />
                    </Stack> : null}
                    {data.method === 'deposit' && hasReadDepositPermission && depositAmount !== undefined && depositAmount === 0 ? <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Text className={'color-red'}>This vendor currently do not have any deposit amount left.</Text>
                    </Stack> : null}
                    {data.method === 'deposit' && hasReadDepositPermission && depositAmount !== undefined && depositAmount < 0 ? <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Text className={'color-red'}>Deposit amount left is currently minus (Rp. {GeneralService.getNumberWithSeparator(depositAmount)})</Text>
                    </Stack> : null}
                    {data.method && data.method !== 'deposit' ? <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack styles={{ root: { width: data.method === 'cash' ? '100%' : '50%' } }}>
                            <SelectBankAccount label={"Transfer from (Bank Account)"}
                                allowCreate={true}
                                selected={data.transferFrom}
                                subsidiaryId={data.invoice?.subsidiary.id || data.payslip?.contract?.subsidiary.id}
                                required
                                onChange={(bankAccount) => {
                                    data.transferFrom = bankAccount;
                                    setData({ ...data });
                                }} />
                        </Stack>
                        {data.method === 'transfer' ? <Stack styles={{ root: { width: '50%' } }}>
                            <SelectBankAccount label={"Transfer to (Bank Account)"}
                                allowCreate={true}
                                selected={data.transferTo}
                                vendorId={data.vendor?.id}
                                subsidiaryId={data.invoice?.targetSubsidiary?.id}
                                employeeId={data.invoice?.targetEmployee?.id || data.payslip?.employee.id}
                                required
                                onChange={(bankAccount) => {
                                    data.transferTo = bankAccount;
                                    setData({ ...data });
                                }} />
                        </Stack> : null}
                        {data.method === 'virtualaccount' ? <Stack styles={{ root: { width: '50%' } }}>
                            <TextField label={'Transfer to (VA Number)'}
                                required
                                value={data.transferToVANumber}
                                onChange={(evt, value) => {
                                    data.transferToVANumber = value || "";
                                    setData({ ...data });
                                }} />
                        </Stack> : null}
                    </Stack> : null}
                    <Stack className='divider'></Stack>
                    <Stack.Item>
                        <DatePicker label={"Due Date"}
                            isRequired={true}
                            value={moment(data.dueDate).toDate()}
                            onSelectDate={(date) => {
                                data.dueDate = date ? moment(date).toISOString() : data.dueDate;
                                setData({ ...data });
                            }}
                            formatDate={GeneralService.formatDate}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Notes"}
                            value={data.notes}
                            multiline
                            rows={3}
                            resizable={false}
                            autoAdjustHeight
                            onChange={(evt, value) => {
                                data.notes = value || "";

                                const validation = ValidationService.combination(value, ['limit'], { maxChars: 100 });
                                error.notes = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.notes}
                            disabled={submitting} />
                    </Stack.Item>
                </> : null
            }
        </Stack>
    </Panel>
};

export default OutcomePaymentForm;
